import { type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { peekPendingPartnerRedirect, setPendingPartnerRedirect } from "../utils/pendingPartnerRedirect";

const REDIRECT_PARAM = "dietitian_portal_redirect_to";

interface PartnerRedirectCaptureProps {
  children: ReactNode;
}

/**
 * Captures `?dietitian_portal_redirect_to=<url>` from any enduser-route URL
 * into sessionStorage. Mounted at the top of the enduser route tree so /login,
 * /register, the landing page, and every other route share a single capture
 * point.
 *
 * The capture is performed synchronously during render rather than in an
 * effect. Two reasons:
 * 1. Subsequent components (e.g. `usePartnerRedirect`'s lazy initial state)
 *    read `peekPendingPartnerRedirect()` during the same render cycle. A
 *    `useEffect` write would run too late.
 * 2. `LoginPage`'s `<Navigate to="/" replace />` (for already-authenticated
 *    users) fires its layout effect synchronously before our regular effect
 *    would, so the navigate-strip approach raced with that and broke.
 *
 * The write is idempotent (guarded by a peek) so it's safe to run on every
 * render, including strict-mode double renders.
 *
 * The param is intentionally NOT stripped from the URL — that strip would
 * race with `LoginPage`'s `<Navigate>` (both `replace: true`) and clobber it.
 * The param falls off naturally on the next legitimate navigation.
 */
export const PartnerRedirectCapture = ({ children }: PartnerRedirectCaptureProps) => {
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get(REDIRECT_PARAM);
  if (redirectTo && peekPendingPartnerRedirect() !== redirectTo) {
    setPendingPartnerRedirect(redirectTo);
  }
  return <>{children}</>;
};
